import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = React.useState({ full_name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = React.useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Access provisioned. Welcome to the Hub.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-white/[0.06] p-12 bg-[#070707] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <Link to="/" className="relative inline-flex items-center gap-3">
          <div className="w-7 h-7 border border-primary grid place-items-center primary-glow"><span className="font-heading font-black text-primary text-sm">D</span></div>
          <div><div className="font-heading font-bold text-sm">DW Mortgage Hub</div><div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-mono">Console</div></div>
        </Link>
        <div className="relative mt-24 max-w-md">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Provision access</div>
          <div className="mt-4 font-heading font-black text-5xl tracking-tighter leading-[0.95]">One topic in. Four platforms out. Zero agency fees.</div>
          <p className="mt-6 text-sm text-muted-foreground">Create your DW Mortgage Hub workspace in under 60 seconds.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <Link to="/" className="lg:hidden inline-flex items-center gap-3 mb-8">
            <div className="w-7 h-7 border border-primary grid place-items-center"><span className="font-heading font-black text-primary text-sm">D</span></div>
            <div className="font-heading font-bold text-sm">DW Mortgage Hub</div>
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Get access</div>
          <h1 className="mt-3 font-heading font-black text-4xl tracking-tighter">Provision your Hub.</h1>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Full name</label>
              <input data-testid="reg-name" required value={form.full_name} onChange={set("full_name")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="Jane Doe" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Email</label>
              <input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="you@lender.co" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Company (optional)</label>
              <input data-testid="reg-company" value={form.company} onChange={set("company")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="Doe Wilson Lending" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Password</label>
              <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" placeholder="Min 6 characters" />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="reg-submit" className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift disabled:opacity-60">
            {loading ? "Provisioning…" : (<>Create workspace <ArrowRight size={16} weight="bold" /></>)}
          </button>

          <div className="mt-6 text-sm text-muted-foreground text-center">
            Already registered? <Link to="/login" data-testid="reg-login-link" className="text-primary hover:opacity-80">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
